import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { usePagination, useSort } from 'core';
import _ from 'lodash';
import { useMemo } from 'react';
import { PaginationBar } from '../table';

export interface ColumnSettings<T extends any> {
  sortKey?: keyof T;
  component: React.ReactNode;
  isNumeric?: boolean;
}
export interface SortSettings {
  colIndex: number | undefined;
  direction: 'asc' | 'desc' | undefined;
}

export interface PaginatedTableProps<T extends any> {
  columns: ColumnSettings<T>[];
  items: T[] | undefined;
  children: (li: T, i: number) => React.ReactNode;
}

export const PaginatedTable = <T = any,>(props: PaginatedTableProps<T>) => {
  const { children, columns, items = [] } = props;

  const [page, setPage] = usePagination();
  const itemCount = useMemo(() => {
    return items?.length || 0;
  }, [items]);

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(itemCount / page.size));
  }, [itemCount, page]);

  const [sort, setSort] = useSort();

  const handlePageChange = (n: number) => {
    setPage({ ...page, page: n });
  };
  const handlePageSizeChange = (size: number) => {
    setPage({ ...page, size });
  };

  const handleSortClick = (columnIndex: number | undefined) => () => {
    let direction = sort?.direction;

    if (sort?.columnIndex !== columnIndex) {
      direction = 'asc';
    } else {
      if (direction === 'asc') {
        direction = 'desc';
      } else {
        columnIndex = undefined;
        direction = undefined;
      }
    }

    if (columnIndex === undefined) {
      setSort(undefined);
    } else {
      setSort({ columnIndex, direction });
    }
  };

  const sortedItems = useMemo(() => {
    const matchingColumnSettings = columns[sort?.columnIndex || -1];
    const dataSet =
      items?.map((item, i) => {
        let key: any = i;

        if (matchingColumnSettings && matchingColumnSettings.sortKey) {
          key = item[matchingColumnSettings.sortKey];
          if (matchingColumnSettings.isNumeric) {
            key = parseFloat(key);
          }
        }

        return { key, item };
      }) || [];

    let sorted = _.sortBy(dataSet, 'key');
    if (sort?.direction === 'desc') {
      sorted = sorted.reverse();
    }

    const start = page.size * (page.page - 1);
    const end = start + start + page.size;
    sorted = sorted.slice(start, end);
    return sorted;
  }, [columns, page, items, sort]);

  const Chevron = (props: { column: number }) => {
    const { column } = props;
    if (column !== sort?.columnIndex) {
      return <></>;
    }

    if (sort.direction === 'asc') {
      return <ChevronUpIcon />;
    } else {
      return <ChevronDownIcon />;
    }
  };

  return (
    <Box>
      <Stack>
        <TableContainer>
          <Table variant="simple">
            <Thead>
              {columns.map((c, i) => (
                <Th key={i}>
                  {(c.sortKey && (
                    <Button
                      width={'100%'}
                      fontSize={'xs'}
                      variant="link"
                      rightIcon={<Chevron column={i} />}
                      onClick={handleSortClick(i)}
                      aria-label={''}
                    >
                      {c.component}
                    </Button>
                  )) ||
                    c.component}
                </Th>
              ))}
            </Thead>
            <Tbody>
              {!sortedItems.length && (
                <Tr>
                  <Td colSpan={columns.length}>
                    <Center>Nothing to Display</Center>
                  </Td>
                </Tr>
              )}
              {sortedItems.map((li, i) => (
                <Tr key={i}>{children(li.item, i)}</Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <PaginationBar
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          itemCount={itemCount}
          page={page.page}
          size={page.size}
          pageCount={pageCount}
        />
      </Stack>
    </Box>
  );
};
